import React from "react";
import { RotateCcw, Play, Pause, Square } from "lucide-react";
import { useAvatarRive } from "./hooks/useAvatarRive";
import { useElevenLabsTTS } from "./hooks/useElevenLabsTTS";
import { useBrowserTTS } from "./hooks/useBrowserTTS";

/**
 * TalkingAvatar
 * - Supports both ElevenLabs (premium) and Browser TTS (free)
 * - Keeps hook order stable (both hooks always called)
 * - Browser TTS only starts after first user interaction (Play)
 *   then auto-reads next messages.
 */
const TalkingAvatar = ({
  user,
  textToSpeak = "",
  autoSpeak = true,
  avatar = "bella",
  voiceProvider = "eleven", // "eleven" | "browser"
  onSpeakChange,
  onBoundary,
}) => {
  const lastSpokenRef = React.useRef("");
  const browserUnlockedRef = React.useRef(false);

  const { rive, RiveComponent } = useAvatarRive(avatar, "State Machine 1");

  const fireRiveTrigger = React.useCallback(
    (triggerName) => {
      if (!rive) return;
      const inputs = rive.stateMachineInputs("State Machine 1") || [];
      const trig = inputs.find((i) => i.name === triggerName);
      if (trig?.fire) trig.fire();
    },
    [rive]
  );

  // ✅ Always call hooks in the same order
  const eleven = useElevenLabsTTS({
    onSpeakChange: (speaking) => {
      if (voiceProvider !== "eleven") return;
      onSpeakChange?.(speaking);
      fireRiveTrigger(speaking ? "talk" : "idle");
    },
    onBoundary: (idx) => {
      if (voiceProvider !== "eleven") return;
      onBoundary?.(idx);
    },
  });

  const browser = useBrowserTTS({
    onSpeakChange: (speaking) => {
      if (voiceProvider !== "browser") return;
      onSpeakChange?.(speaking);
      fireRiveTrigger(speaking ? "talk" : "idle");
    },
    onBoundary: (idx) => {
      if (voiceProvider !== "browser") return;
      onBoundary?.(idx);
    },
  });

  const tts = voiceProvider === "eleven" ? eleven : browser;

  // When switching providers: stop both to avoid overlap
React.useEffect(() => {
  if (voiceProvider === "eleven") {
    browser.stop?.();
  } else {
    eleven.stop?.();
  }
  lastSpokenRef.current = ""; // ✅ important
}, [voiceProvider, browser, eleven]);



  // Auto-speak:
  // - Eleven: always
  // - Browser: only after first user click (gesture unlock)
const speakingRef = React.useRef(false);

React.useEffect(() => {
  if (!autoSpeak) return;

  const next = (textToSpeak || "").trim();
  if (!next) return;

  // prevent duplicates
  if (next === lastSpokenRef.current) return;

  // prevent overlapping calls
  if (speakingRef.current) return;

  lastSpokenRef.current = next;
  speakingRef.current = true;

  const run = async () => {
    try {
      if (voiceProvider === "eleven") {
        await eleven.speak(next);
      } else {
        await browser.speak(next);
      }
    } catch (e) {
      console.warn("TTS error:", e);
    } finally {
      speakingRef.current = false;
    }
  };

  run();
}, [textToSpeak, autoSpeak, voiceProvider]);





const handlePlayPause = () => {
  const next = (textToSpeak || "").trim();
  if (!next) return;

  if (tts.isPlaying) {
    tts.pause?.();
    return;
  }

  if (voiceProvider === "browser") {
    browserUnlockedRef.current = true;
    browser.stop?.();
    browser.speak(next);
    return;
  }

  if (tts.hasLastAudio) {
    tts.resume?.();
  } else {
    tts.speak(next);
  }
};




  return (
    <div
      style={{
        width: 320,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
      }}
    >
      {/* Big avatar */}
      <RiveComponent style={{ width: "100%", height: "100%", display: "block" }} />

      {/* Player pill */}
      <div
        style={{
          width: "100%",
          borderRadius: 999,
          padding: "10px 12px",
          border: "1px solid rgba(20,20,40,0.10)",
          background: "rgba(255,255,255,0.55)",
          backdropFilter: "blur(12px)",
          boxShadow: "0 12px 30px rgba(20, 20, 40, 0.10)",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <button
          type="button"
          onClick={handlePlayPause}
          aria-label={tts.isPlaying ? "Pause" : "Play"}
          style={iconBtnStyle}
        >
          {tts.isPlaying ? <Pause size={18} /> : <Play size={18} />}
        </button>

        <div
          style={{
            flex: 1,
            height: 8,
            borderRadius: 999,
            background: "rgba(20,20,40,0.10)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.round((tts.progress || 0) * 100)}%`,
              borderRadius: 999,
              background: "rgba(123, 97, 255, 0.95)",
              transition: "width 120ms linear",
            }}
          />
        </div>

        <button
          type="button"
          onClick={tts.replay}
          aria-label="Replay"
          disabled={!tts.hasLastAudio}
          style={{
            ...iconBtnStyle,
            opacity: tts.hasLastAudio ? 1 : 0.45,
            cursor: tts.hasLastAudio ? "pointer" : "default",
          }}
          title="Replay"
        >
          <RotateCcw size={18} />
        </button>

        <button type="button" onClick={tts.stop} aria-label="Stop" style={iconBtnStyle} title="Stop">
          <Square size={18} />
        </button>
      </div>
    </div>
  );
};

const iconBtnStyle = {
  width: 36,
  height: 36,
  borderRadius: 999,
  border: "1px solid rgba(20,20,40,0.10)",
  background: "rgba(255,255,255,0.45)",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

export default TalkingAvatar;
